import { Matchup, Match, LineupPlayerOption } from '@/entities';
import { TieBreakerSettings, TieBreakerTeamRotation } from '@/entities/Matchup';
import { MatchCompletedEnum } from '@/entities/matchup/constants';
import { SequenceStatus, TieBreakerSequence } from '@/entities/rotations/GameSequence';
import { Rotation, Sequence } from '@/entities/rotations/LastSequence';
import { RotationPlayerInfo } from '@/entities/rotations/RotationsInfo';
import { LineupEntryOrder } from '@/entities/season-division/SeasonDivisionPool';
import { CreateGameSequencesPayload, GameSequenceItem } from '@/services/actions/tie-breaker/rotations';
import { TieBreakerPair } from '@/utils/types';

export enum PlayerGender {
	MENS = 'M',
	WOMENS = 'F'
}

export const isValidScore = (val: string): boolean => {
	if (!/^\d{0,2}$/.test(val)) return false;
	const num = Number(val);
	return val === '' || (num >= 0 && num <= 99);
};

export const isValidMatchResult = (scoreOne: number | '', scoreTwo: number | '', winBy: number): boolean => {
	if (typeof scoreOne !== 'number' || typeof scoreTwo !== 'number') return false;
	const diff = Math.abs(scoreOne - scoreTwo);
	return diff >= winBy;
};

const groupGenderMap: Record<string, string> = {
	Mens: 'M',
	Womens: 'F',
	Mixed: 'MF',
	Coed: 'MF'
};

export function isPlayerDisabled(gender: 'M' | 'F', groupTitle: string): boolean {
	const allowedGenders = groupGenderMap[groupTitle];
	if (!allowedGenders) return false;
	return !allowedGenders.includes(gender);
}

/**
 * How many lines (rotations) the tiebreaker lineup should hold, per the division's rotation settings.
 *
 * Only the "leading team reaches an even score" mode encodes this today: each listed score is one
 * rotation, and the lineup that plays after the last rotation is one more line. So "2,4,6,8,10" in a
 * game to 11 means 6 lines. The other two rotation modes derive from live score rather than a fixed
 * list, so they return undefined and callers keep their existing behaviour.
 */
export const getTieBreakerLineCount = (settings: TieBreakerSettings | undefined): number | undefined => {
	if (settings?.tieBreakerTeamRotation !== TieBreakerTeamRotation.TEAM_ROTATION_LEADING_TEAM_REACHED_SCORE) return undefined;

	const rotationScores = (settings.tieBreakerRotateAfterLeadingTeamReachesPoints ?? '')
		.split(/[,;]/)
		.map((score) => score.trim())
		.filter((score) => /^\d+$/.test(score) && Number(score) > 0);

	return rotationScores.length ? rotationScores.length + 1 : undefined;
};

export const getLegitMatches = (matches: Match[]): Match[] => {
	return matches.filter((match) => {
		const isNotTieBreaker = match.isTieBreaker !== true;
		const isDifferentCompletedType = match.matchCompletedType !== 4 && match.matchCompletedType !== 6;
		return isNotTieBreaker && isDifferentCompletedType;
	});
};

export const areAllLegitMatchesPlayed = (matches: Match[]): boolean => {
	const legitMatches = matches.filter((match) => match.isTieBreaker !== true);

	const notCompleted = legitMatches.filter(
		(match) =>
			match.matchStatus !== MatchCompletedEnum.WITHDRAWAL_MATCH_COMPLETED_TYPE ||
			![MatchCompletedEnum.PLAYED_MATCH_COMPLETED_TYPE, MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE].includes(
				match.matchCompletedType ?? -1
			)
	);

	return notCompleted.length === 0;
};

export const isMatchTied = (matchupData: Matchup): boolean => {
	if (!matchupData) return false;

	const { winner, teamOneScore, teamTwoScore } = matchupData;

	return winner === 0 && typeof teamOneScore === 'number' && typeof teamTwoScore === 'number' && teamOneScore === teamTwoScore && teamOneScore > 0;
};

export const hasTieBreaker = (matches: Match[]): boolean => {
	return matches.some((match) => match.isTieBreaker === true);
};

export const hasUnplayedTieBreaker = (matches: Match[]): boolean => {
	return matches.some((match) => match.isTieBreaker === true && match.matchCompleted === null);
};

export const getLastActiveSequence = (sequences: TieBreakerSequence[]) =>
	[...sequences].reverse().find((seq) => seq.sequenceStatus === SequenceStatus.PLAYED || seq.sequenceStatus === SequenceStatus.IN_PROGRESS);

export type MoveDirection = 'up' | 'down';

export function reorderArrayItem<T>(array: T[], index: number, direction: MoveDirection): T[] {
	const targetIndex = direction === 'up' ? index - 1 : index + 1;
	if (targetIndex < 0 || targetIndex >= array.length) return array;

	const updated = [...array];
	const temp = updated[index]!;
	updated[index] = updated[targetIndex]!;
	updated[targetIndex] = temp;

	return updated;
}

type PlayerBasic = {
	uuid: string;
	fullName: string;
};

export type PlayedMatch = {
	uuid: string;
	left: PlayerBasic;
	right: PlayerBasic;
	leftScore: number;
	rightScore: number;
};

export const mapPlayedSequencesToMatches = (sequences: TieBreakerSequence[]): PlayedMatch[] => {
	return sequences
		.filter(
			(
				s
			): s is TieBreakerSequence & {
				teamOnePlayerOneName: string;
				teamTwoPlayerOneName: string;
			} => s.sequenceStatus === SequenceStatus.PLAYED && !!s.teamOnePlayerOneName && !!s.teamTwoPlayerOneName
		)
		.map((s) => ({
			uuid: s.uuid,
			left: {
				uuid: s.teamTwoPlayerOneUuid,
				fullName: s.teamTwoPlayerOneName,
				secondPlayerName: s.teamTwoPlayerTwoName ?? undefined
			},
			right: {
				uuid: s.teamOnePlayerOneUuid,
				fullName: s.teamOnePlayerOneName,
				secondPlayerName: s.teamOnePlayerTwoName ?? undefined
			},
			leftScore: s.teamTwoScore ?? 0,
			rightScore: s.teamOneScore ?? 0
		}));
};

interface TotalScore {
	teamOneTotal: number;
	teamTwoTotal: number;
}

export const getTotalScoresFromSequences = (sequences: TieBreakerSequence[]): TotalScore => {
	return sequences.reduce(
		(acc, seq) => {
			return {
				teamOneTotal: acc.teamOneTotal + (seq.teamOneScore ?? 0),
				teamTwoTotal: acc.teamTwoTotal + (seq.teamTwoScore ?? 0)
			};
		},
		{ teamOneTotal: 0, teamTwoTotal: 0 }
	);
};

export const getWinner = (teamOneTotal: number, teamTwoTotal: number, game1Max: number, game1WinBy: number): 'teamOne' | 'teamTwo' | null => {
	const teamOneWon = teamOneTotal >= game1Max && teamOneTotal - teamTwoTotal >= game1WinBy;
	const teamTwoWon = teamTwoTotal >= game1Max && teamTwoTotal - teamOneTotal >= game1WinBy;

	if (teamOneWon) return 'teamOne';
	if (teamTwoWon) return 'teamTwo';

	return null;
};

type GetNextUpParams = {
	team: Rotation[];
	currentPlayerUuid: string;
	rotateOut: boolean;
	format: 'SINGLES' | 'DOUBLES';
};

export function getNextUpTeam({ team, currentPlayerUuid, rotateOut, format }: GetNextUpParams): Rotation[] {
	let result: Rotation[];

	if (!rotateOut) {
		result = team.filter((p) => p.playerOneUuid === currentPlayerUuid);
	} else {
		result = team.filter((p) => p.playerOneUuid !== currentPlayerUuid);
	}

	if (format === 'SINGLES' && result.length === 0) {
		const current = team.find((p) => p.playerOneUuid === currentPlayerUuid);
		if (current) {
			result = [current];
		}
	}

	if (format === 'DOUBLES' && result.length === 0) {
		const current = team.find((p) => p.playerOneUuid === currentPlayerUuid);
		if (current?.playerTwoUuid) {
			result = [current];
		}
	}

	return result;
}

export const getTieBreakerMatch = (matches: Match[]): Match | undefined => {
	return matches.find((match) => match.isTieBreaker === true);
};

export const getNextInLineup = (arr: RotationPlayerInfo[]) =>
	(arr.length >= 2 ? arr.slice(0, -1) : arr).map((r) => ({
		playerOneName: r.playerOneName,
		playerTwoName: r.playerTwoName ?? undefined,
		rotationUuid: r.rotationUuid
	}));

export function getNewDoublesPairs(data: (string | TieBreakerPair)[], existingPairKeys: string[]): TieBreakerPair[] {
	return data.filter((pair): pair is TieBreakerPair => {
		if (typeof pair !== 'object' || pair === null) return false;

		const key = [pair.player1, pair.player2].filter(Boolean).sort().join('-');
		return !existingPairKeys.includes(key);
	});
}
export function getNewSinglesPlayers(data: (string | TieBreakerPair)[], existingUuids: string[]): string[] {
	return data.filter((item): item is string => typeof item === 'string' && !existingUuids.includes(item));
}

export const extractTieBreakerInfo = (matchupData?: Matchup) => {
	const match = matchupData?.matches.find((m) => m.isTieBreaker);

	return {
		match,
		id: match?.matchUuid || '',
		status: match?.matchStatus || 0,
		tieBreakerTitle: match?.roundText || 'TieBreaker',
		matchCompletedType: match?.matchCompletedType
	};
};

interface BuildFullPayloadParams {
	teamOneScore: number;
	teamTwoScore: number;
	gameSequences: GameSequenceItem[];
	lastSequence: Sequence;
	sequences: TieBreakerSequence[];
	teamOneNextUp: Rotation[];
	teamTwoNextUp: Rotation[];
	isDoubles: boolean;
	editedScores?: {
		uuid: string;
		teamOneScore: number;
		teamTwoScore: number;
	}[];
}

export const buildSequencePayloadForSave = ({
	teamOneScore,
	teamTwoScore,
	gameSequences,
	lastSequence,
	sequences,
	teamOneNextUp,
	teamTwoNextUp,
	isDoubles,
	editedScores
}: BuildFullPayloadParams): CreateGameSequencesPayload => {
	const baseMatchUuid = lastSequence.matchUuid;
	const gameNumber = lastSequence.gameNumber;
	const currentNumber = lastSequence.sequenceNumber;

	const firstSequenceUnplayed = sequences[0]?.teamOneScore === 0 && sequences[0]?.teamTwoScore === 0;

	const lastSequenceInProgress = lastSequence.sequenceStatus === SequenceStatus.IN_PROGRESS;

	const updatedSequences = gameSequences.map((seq) => {
		const edited = editedScores?.find((e) => e.uuid === seq.uuid);

		if (!edited) return seq;

		return {
			...seq,
			teamOneScore: edited.teamOneScore ?? 0,
			teamTwoScore: edited.teamTwoScore ?? 0,
			sequenceStatus: SequenceStatus.PLAYED,
			teamOnePlayerTwoUuid: seq.teamOnePlayerTwoUuid ?? undefined,
			teamTwoPlayerTwoUuid: seq.teamTwoPlayerTwoUuid ?? undefined
		};
	});

	const newSequence: GameSequenceItem = {
		matchUuid: baseMatchUuid,
		sequenceNumber:
			lastSequence.sequenceStatus === SequenceStatus.IN_PROGRESS || lastSequence.sequenceStatus === SequenceStatus.NOT_PLAYED
				? currentNumber
				: currentNumber + 1,
		gameNumber,
		teamOneScore: typeof teamOneScore === 'number' ? teamOneScore : 0,
		teamTwoScore: typeof teamTwoScore === 'number' ? teamTwoScore : 0,
		sequenceStatus: SequenceStatus.PLAYED,
		teamOnePlayerOneUuid:
			lastSequenceInProgress || firstSequenceUnplayed ? (lastSequence?.teamOnePlayerOneUuid ?? '') : (teamOneNextUp[0]?.playerOneUuid ?? ''),
		teamTwoPlayerOneUuid:
			lastSequenceInProgress || firstSequenceUnplayed ? (lastSequence?.teamTwoPlayerOneUuid ?? '') : (teamTwoNextUp[0]?.playerOneUuid ?? '')
	};

	if (isDoubles) {
		newSequence.teamOnePlayerTwoUuid =
			lastSequenceInProgress || firstSequenceUnplayed ? (lastSequence?.teamOnePlayerTwoUuid ?? '') : (teamOneNextUp[0]?.playerTwoUuid ?? '');
		newSequence.teamTwoPlayerTwoUuid =
			lastSequenceInProgress || firstSequenceUnplayed ? (lastSequence?.teamTwoPlayerTwoUuid ?? '') : (teamTwoNextUp[0]?.playerTwoUuid ?? '');
	}

	if (editedScores?.length) {
		return {
			sequences: updatedSequences
		};
	}

	return {
		sequences:
			sequences[0]?.teamOneScore === 0 && sequences[0].teamTwoScore === 0
				? [{ ...newSequence, sequenceNumber: 1, sequenceStatus: SequenceStatus.PLAYED }]
				: lastSequence.sequenceStatus !== SequenceStatus.PLAYED
					? [...updatedSequences.slice(0, -1), newSequence]
					: [...updatedSequences, newSequence]
	};
};

interface ShouldShowTieBreakerParams {
	permissionsStore: { gamedayAccess: boolean };
	isAwayTeamCaptain: boolean;
	isHomeTeamCaptain: boolean;
	hasTeamOneTieBreakerLineup: boolean;
	hasTeamTwoTieBreakerLineup: boolean;
	poolData?: {
		lineupEntryOrder?: LineupEntryOrder;
	};
}

export const shouldShowTieBreakerButton = ({
	permissionsStore,
	isAwayTeamCaptain,
	isHomeTeamCaptain,
	hasTeamOneTieBreakerLineup,
	hasTeamTwoTieBreakerLineup,
	poolData
}: ShouldShowTieBreakerParams): boolean => {
	if (permissionsStore.gamedayAccess || isAwayTeamCaptain || isHomeTeamCaptain) return true;

	if (isAwayTeamCaptain && !hasTeamTwoTieBreakerLineup) {
		if (
			poolData?.lineupEntryOrder === LineupEntryOrder.AWAY_TEAM_FIRST_LINEUP_ENTRY ||
			poolData?.lineupEntryOrder === LineupEntryOrder.EITHER_TEAM_FIRST_LINEUP_ENTRY
		) {
			return true;
		}

		if (poolData?.lineupEntryOrder === LineupEntryOrder.HOME_TEAM_FIRST_LINEUP_ENTRY && hasTeamOneTieBreakerLineup) {
			return true;
		}
	}

	if (isHomeTeamCaptain && !hasTeamOneTieBreakerLineup) {
		if (
			poolData?.lineupEntryOrder === LineupEntryOrder.HOME_TEAM_FIRST_LINEUP_ENTRY ||
			poolData?.lineupEntryOrder === LineupEntryOrder.EITHER_TEAM_FIRST_LINEUP_ENTRY
		) {
			return true;
		}

		if (poolData?.lineupEntryOrder === LineupEntryOrder.AWAY_TEAM_FIRST_LINEUP_ENTRY && hasTeamTwoTieBreakerLineup) {
			return true;
		}
	}

	return false;
};

export function sortTieBreakerSinglesPlayers(
	players: LineupPlayerOption[],
	rotationsInfo: RotationPlayerInfo[] | undefined,
	selectedUuids: string[]
): LineupPlayerOption[] {
	if (!players.length) return [];

	const rotationOrderByUserUuid = new Map<string, number>();
	(rotationsInfo ?? []).forEach((r) => rotationOrderByUserUuid.set(r.playerOneUuid, r.rotationOrder));

	const selectedSet = new Set(selectedUuids);

	const getOrder = (p: LineupPlayerOption) => rotationOrderByUserUuid.get(p.userUuid)!;

	const selected: LineupPlayerOption[] = [];
	const unselected: LineupPlayerOption[] = [];

	for (const p of players) {
		(selectedSet.has(p.userUuid) ? selected : unselected).push(p);
	}

	selected.sort((a, b) => getOrder(a) - getOrder(b));
	unselected.sort((a, b) => getOrder(a) - getOrder(b));

	return [...selected, ...unselected];
}

export const generatePrintFilename = (matchupData: Matchup) => {
	const today = new Date();
	const dateStr = today.toLocaleDateString('en-CA').replace(/-/g, '');
	const awayTeam = matchupData.teamTwoTitle?.replace(/[^a-zA-Z0-9]/g, '_') || 'AwayTeam';
	const homeTeam = matchupData.teamOneTitle?.replace(/[^a-zA-Z0-9]/g, '_') || 'HomeTeam';
	return `${awayTeam}_vs_${homeTeam}_TieBreakerLineup_${dateStr}.pdf`;
};

export const getNextInList = (teamPlayers: Rotation[], lastSequenceInProgress: boolean, firstSequenceUnplayed: boolean) => {
	const reordered = lastSequenceInProgress || firstSequenceUnplayed ? [...teamPlayers] : [...teamPlayers.slice(1), teamPlayers[0]];

	return reordered.map((r) => ({
		playerOneName: r?.playerOneName ?? '',
		playerTwoName: r?.playerTwoName ?? undefined,
		rotationUuid: r?.rotationUuid ?? ''
	}));
};
